'use client';

import React, { useState, useEffect, useRef } from 'react';
import { ExtendedArchiMateDiagram } from '@/lib/archimate/extended-types';
import { ArchiMateElement } from '@/lib/archimate/types';
import {
  parseNaturalLanguageQuery,
  executeQuery,
  getSuggestedQueries,
} from '@/lib/archimate/query-utils';
import { Search, X, Sparkles, Lightbulb } from 'lucide-react';

interface QueryBarProps {
  isOpen: boolean;
  diagram: ExtendedArchiMateDiagram;
  onClose: () => void;
  onResults: (elements: ArchiMateElement[]) => void;
  onSelectElement: (elementId: string) => void;
}

export default function QueryBar({
  isOpen,
  diagram,
  onClose,
  onResults,
  onSelectElement,
}: QueryBarProps) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<ArchiMateElement[]>([]);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const suggestions = getSuggestedQueries(diagram);

  // Focus input when opened
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
      setShowSuggestions(true);
    }
  }, [isOpen]);

  // Execute query when it changes
  useEffect(() => {
    if (query.trim()) {
      const queryResult = executeQuery(query, diagram);
      setResults(queryResult.elements);
      setSelectedIndex(0);
      setShowSuggestions(false);
      onResults(queryResult.elements);
    } else {
      setResults([]);
      setShowSuggestions(true);
      onResults([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, diagram]);

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!isOpen) return;

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          if (showSuggestions) {
            setSelectedIndex((prev) => Math.min(prev + 1, suggestions.length - 1));
          } else {
            setSelectedIndex((prev) => Math.min(prev + 1, results.length - 1));
          }
          break;
        case 'ArrowUp':
          e.preventDefault();
          setSelectedIndex((prev) => Math.max(prev - 1, 0));
          break;
        case 'Enter':
          e.preventDefault();
          if (showSuggestions && suggestions[selectedIndex]) {
            setQuery(suggestions[selectedIndex]);
            setShowSuggestions(false);
          } else if (results[selectedIndex]) {
            handleSelectElement(results[selectedIndex]);
          }
          break;
        case 'Escape':
          e.preventDefault();
          if (query) {
            setQuery('');
            setShowSuggestions(true);
          } else {
            onClose();
          }
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, results, selectedIndex, showSuggestions, suggestions, query]);

  const handleSelectElement = (element: ArchiMateElement) => {
    onSelectElement(element.id);
    setQuery('');
    onClose();
  };

  const handleSelectSuggestion = (suggestion: string) => {
    setQuery(suggestion);
    setShowSuggestions(false);
    inputRef.current?.focus();
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black/30 flex items-start justify-center pt-20 z-[100]"
      onClick={onClose}
    >
      <div
        className="bg-background border-2 border-accent rounded-lg w-full max-w-3xl shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Search Input */}
        <div className="flex items-center gap-3 p-4 border-b border-border">
          <Sparkles className="w-5 h-5 text-accent flex-shrink-0" />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Ask anything... (e.g., 'show all application components in the application layer')"
            className="flex-1 bg-transparent text-foreground placeholder-foreground-muted focus:outline-none text-lg"
          />
          {query && (
            <button
              onClick={() => {
                setQuery('');
                setShowSuggestions(true);
              }}
              className="p-1 hover:bg-cream/10 rounded transition-colors"
            >
              <X className="w-4 h-4 text-foreground-tertiary" />
            </button>
          )}
          <button
            onClick={onClose}
            className="p-1 hover:bg-cream/10 rounded transition-colors"
          >
            <X className="w-5 h-5 text-foreground" />
          </button>
        </div>

        {/* Suggestions */}
        {showSuggestions && suggestions.length > 0 && (
          <div className="max-h-[400px] overflow-y-auto">
            <div className="p-4 border-b border-border-muted">
              <div className="flex items-center gap-2 text-sm text-foreground-tertiary mb-3">
                <Lightbulb className="w-4 h-4" />
                Suggested Queries
              </div>
              <div className="space-y-1">
                {suggestions.map((suggestion, index) => (
                  <div
                    key={index}
                    onClick={() => handleSelectSuggestion(suggestion)}
                    className={`p-3 rounded-lg cursor-pointer transition-colors ${
                      index === selectedIndex
                        ? 'bg-accent-muted border-l-4 border-l-orange'
                        : 'hover:bg-cream/5'
                    }`}
                  >
                    <div className="font-medium text-foreground text-sm">{suggestion}</div>
                  </div>
                ))}
              </div>
            </div>

            <div className="p-3 bg-cream/5 text-center text-xs text-foreground-tertiary">
              Try natural language queries like &quot;show databases&quot;, &quot;find high cost elements&quot;, or &quot;list
              security vulnerabilities&quot;
            </div>
          </div>
        )}

        {/* Results */}
        {!showSuggestions && (
          <div className="max-h-[400px] overflow-y-auto">
            {query && results.length === 0 ? (
              <div className="p-8 text-center text-foreground-tertiary">
                <p>No elements found matching &quot;{query}&quot;</p>
                <p className="text-xs text-foreground-muted mt-2">
                  Try different keywords or check the suggestions
                </p>
              </div>
            ) : query && results.length > 0 ? (
              <div>
                {results.map((element, index) => (
                  <div
                    key={element.id}
                    onClick={() => handleSelectElement(element)}
                    className={`p-4 border-b border-border-muted cursor-pointer transition-colors ${
                      index === selectedIndex
                        ? 'bg-accent-muted border-l-4 border-l-orange'
                        : 'hover:bg-cream/5'
                    }`}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <h3 className="font-medium text-foreground mb-1">{element.name}</h3>
                        <div className="flex items-center gap-3 text-sm text-foreground-tertiary">
                          <span className="capitalize">{element.type.replace(/-/g, ' ')}</span>
                          <span>•</span>
                          <span className="capitalize">{element.layer}</span>
                        </div>
                        {element.documentation && (
                          <p className="text-sm text-foreground-secondary mt-2 line-clamp-2">
                            {element.documentation}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
                <div className="p-3 bg-cream/5 text-center text-xs text-foreground-tertiary">
                  {results.length} result{results.length !== 1 ? 's' : ''} • Use ↑↓ to navigate, Enter
                  to select
                </div>
              </div>
            ) : null}
          </div>
        )}

        {/* Keyboard Hints */}
        <div className="px-4 py-2 bg-cream/5 border-t border-border-muted flex items-center justify-between text-xs text-foreground-tertiary">
          <span>Natural language queries powered by intelligent parsing</span>
          <span>↑↓ Navigate • ↵ Select • ESC Clear/Close</span>
        </div>
      </div>
    </div>
  );
}
